import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Video, ImagePlus, X } from "lucide-react";
import { createPost, IPost, uploadImageToStorage } from "@/lib/community";
import { v4 as uuidv4 } from "uuid";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableMediaItem from "@/components/community/SortableMediaItem";
import { cn } from "@/lib/utils";
import { getEmbeddedVideoUrl } from "@/utils/getEmbed";
import { useSubscription } from "@/contexts/SubscriptionContext";
import CustomAvatar from "./CustomAvatar";
import { useTranslation } from "react-i18next";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { MixpanelActions } from "../../integrations/mixpanel/mixpanel";

interface IProps {
  onPostAdd: (post: IPost) => void;
}

export default function PostForm({ onPostAdd }: IProps) {
  const { t } = useTranslation();
  const { subscription } = useSubscription();
  const [formClicked, setFormClicked] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [media, setMedia] = useState<
    { id: string; type: "image" | "video"; url: string }[]
  >([]);
  const formRef = useRef<HTMLDivElement>(null);
  useOutsideClick(formRef, () => setFormClicked(false));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const handlePost = async () => {
    if (!title || !content) return;
    const postData = {
      author: subscription.user_id,
      title,
      content,
      media,
    };
    const post = await createPost(postData);
    MixpanelActions.createPost(postData);
    setTitle("");
    if (post)
      onPostAdd({
        ...post,
        authorData: {
          username: subscription.username,
          avatar: subscription.avatar,
        },
      });
    setContent("");
    setMedia([]);
    setFormClicked(false);
  };

  const handleAddVideo = () => {
    const url = videoUrlInput.trim();

    if (!url) return;

    const isDirectVideo = url.match(/\.(mp4|webm|ogg)(\?.*)?$/i);

    const embedUrl = getEmbeddedVideoUrl(url);

    if (isDirectVideo || embedUrl) {
      setMedia((prev) => [
        ...prev,
        { id: uuidv4(), type: "video", url: isDirectVideo ? url : embedUrl },
      ]);
      setVideoModalOpen(false);
      setVideoUrlInput("");
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      const imageUrl = await uploadImageToStorage(file);
      if (imageUrl) {
        setMedia((prev) => [
          ...prev,
          { id: uuidv4(), type: "image", url: imageUrl },
        ]);
      }
    }
  };

  const triggerImageUpload = () => fileInputRef.current?.click();

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setIsDragging(false);

    if (active.id !== over?.id) {
      setMedia((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const removeMedia = (id: string) => {
    setMedia((prev) => prev.filter((item) => item.id !== id));
  };

  const handleScrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };
  // useEffect(() => {
  //   if (formClicked) {
  //     document.body.style.overflow = "hidden";
  //   } else {
  //     document.body.style.overflow = "";
  //   }

  //   return () => {
  //     document.body.style.overflow = "";
  //   };
  // }, [formClicked]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  return (
    <>
      {!formClicked ? (
        <Card
          className="shadow-md rounded-xl bg-background"
          onClick={() => { setFormClicked(true); handleScrollToForm(); }}
        >
          <div className="py-2.5 px-4 flex items-center gap-4">
            <CustomAvatar
              userData={{
                username: subscription.username,
                avatar: subscription.avatar,
                user_rating: subscription.user_rating,
              }}
            />
            <div className="text-gray-500 font-bold text-xl">
              {t("community.postForm.writePost")}
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="absolute w-screen h-[300vh] top-0 left-0 opacity-50 bg-gray-400 z-[2]" />
          <CardContent
            ref={formRef}
            className="p-4 space-y-4 relative shadow-md rounded-xl bg-background z-[3]"
          >
            <div className="flex gap-2 items-center">
              <CustomAvatar
                userData={{
                  username: subscription.username,
                  avatar: subscription.avatar,
                  user_rating: subscription.user_rating,
                }}
              />
              <div className="text-sm text-gray-600">
                <strong>{subscription.username}</strong>
              </div>
            </div>
            <Input
              placeholder={t("community.postForm.titlePlaceholder")}
              className="text-lg font-semibold"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              placeholder={t("community.postForm.contentPlaceholder")}
              rows={4}
              className="resize-none"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <input
              type="file"
              multiple
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
            />
            {media.length > 0 && (
              <DndContext
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={media}
                  strategy={horizontalListSortingStrategy}
                >
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {media.map((item) => (
                      <div key={item.id} className="relative group w-40 h-40">
                        <SortableMediaItem id={item.id}>
                          <div className="w-40 h-40">
                            {item.type === "image" ? (
                              <img
                                src={item.url}
                                alt="Uploaded"
                                className="rounded-lg w-full h-full object-cover"
                              />
                            ) : item.url.includes("embed") ? (
                              <iframe
                                src={item.url}
                                className="rounded-lg w-full h-full object-cover"
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                              />
                            ) : (
                              <video
                                src={item.url}
                                controls
                                className="rounded-lg w-full h-full object-cover"
                              />
                            )}
                          </div>
                        </SortableMediaItem>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "absolute top-2 right-2 w-6 h-6 bg-white/80 z-10",
                            isDragging && "hidden"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMedia(item.id);
                          }}
                        >
                          <X className="h-4 w-4 text-gray-700" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            <div className="w-full flex justify-between flex-wrap">
              <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setVideoModalOpen(true)}
                >
                  <Video />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={triggerImageUpload}
                >
                  <ImagePlus />
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTitle("");
                    setContent("");
                    setMedia([]);
                    setFormClicked(false);
                  }}
                >
                  {t("community.postForm.cancel")}
                </Button>
                <Button onClick={handlePost} disabled={!title || !content}>
                  {t("community.postForm.post")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {videoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Add Video Link</h2>
            <Input
              placeholder="https://example.com/video"
              value={videoUrlInput}
              onChange={(e) => setVideoUrlInput(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setVideoModalOpen(false)}>
                {t("community.postForm.cancel")}
              </Button>
              <Button onClick={handleAddVideo} disabled={!videoUrlInput.trim()}>
                {t("community.postForm.insert")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
